import { Check, CornerUpRight, Loader2, X } from 'lucide-react'
import { useState } from 'react'

import type { ChatResolutionStatus } from '@/lib/api/support-chat-types'

interface EndSessionPromptProps {
  onSubmit: (resolution: ChatResolutionStatus | null) => Promise<void>
  onCancel: () => void
}

interface Option {
  value: ChatResolutionStatus
  label: string
  detail: string
  icon: React.ReactNode
}

const OPTIONS: Option[] = [
  {
    value: 'resolved',
    label: 'Resolved',
    detail: 'Issue is sorted — attendee should be back up and running.',
    icon: <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />,
  },
  {
    value: 'unresolved',
    label: 'Unresolved',
    detail: "Couldn't fix it on this call — attendee will follow up.",
    icon: <X className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />,
  },
  {
    value: 'escalated',
    label: 'Escalated to EventPay',
    detail: 'Needs the EventPay team — sending it up.',
    icon: <CornerUpRight className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />,
  },
]

export function EndSessionPrompt({ onSubmit, onCancel }: EndSessionPromptProps) {
  const [submitting, setSubmitting] = useState<ChatResolutionStatus | 'skip' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handle = async (value: ChatResolutionStatus | null) => {
    if (submitting) return
    setError(null)
    setSubmitting(value ?? 'skip')
    try {
      await onSubmit(value)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end session')
      setSubmitting(null)
    }
  }

  return (
    <div className="absolute inset-0 z-10 flex items-end bg-black/30 sm:items-center sm:justify-center">
      <div className="w-full rounded-t-lg bg-white p-4 shadow-xl dark:bg-[#1a1a1a] sm:max-w-sm sm:rounded-lg">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          Was this resolved?
        </h3>
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          Two seconds of feedback helps us improve. Skips don&apos;t hurt — they just don&apos;t count.
        </p>

        <div className="mt-4 space-y-2">
          {OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={!!submitting}
              onClick={() => handle(opt.value)}
              className="flex w-full items-start gap-3 rounded-md border border-neutral-200 px-3 py-2 text-left transition-colors hover:border-neutral-300 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-800 dark:hover:border-neutral-700 dark:hover:bg-neutral-900"
            >
              <span className="mt-0.5">
                {submitting === opt.value ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-500" />
                ) : (
                  opt.icon
                )}
              </span>
              <span className="flex-1">
                <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {opt.label}
                </span>
                <span className="block text-[11px] text-neutral-500 dark:text-neutral-400">
                  {opt.detail}
                </span>
              </span>
            </button>
          ))}
        </div>

        {error && <p className="mt-3 text-[11px] text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => handle(null)}
            disabled={!!submitting}
            className="text-[11px] text-neutral-500 hover:text-neutral-700 disabled:opacity-50 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            Skip — close without recording
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={!!submitting}
            className="text-[11px] text-neutral-500 hover:text-neutral-700 disabled:opacity-50 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            Keep chatting
          </button>
        </div>
      </div>
    </div>
  )
}
